/*
This file is located in the main branch
Last time the file was updated (UTC): [05.09.2024 11:54:04]
Permissions to update outside the main branch: [false]
*/

import { Button, Input, useErrorState, ValidatedInput } from '@finbeat/react-toolkit';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosError } from 'axios';
import { getDefaultReferalLinks } from 'axios/leadgenApi';
import useApi from 'hooks/useApi';
import { t } from 'i18next';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { LeadgenInfo } from 'models/api/leadgen';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useQuery } from 'react-query';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';

const emptyItem = {
    id: null,
    utmTag: '',
    utmValue: '',
    isDeleted: false,
};

type LeadgenModalReferallinksProps = { disabled: boolean };

const LeadgenModalReferallinks = ({ disabled }: LeadgenModalReferallinksProps) => {
    const { setValue, getValues, watch } = useFormContext<LeadgenInfo>();
    const { instanceRoot } = useGeneralSettingsContext();

    const getRefLink = () =>
        `${getValues().urlBase || instanceRoot + '/'}?${getValues()
            .referrallinks.filter((item) => !item.isDeleted && item.utmTag && item.utmValue)
            .map((item) => `${item.utmTag}=${item.utmValue}`)
            .join('&')}&leadname_id={clickid}`;

    const [refLink, setRefLink] = useState<string | null>(getRefLink());

    const refreshRefLink = () => {
        setRefLink(getRefLink);
    };

    const api = useApi();
    const { handleRequestError } = useErrorState();

    const getDefaultReferalLinksQuery = useQuery(
        'defaultReferalLinks',
        () => {
            const leadgenName = getValues().name;
            return getDefaultReferalLinks(api, leadgenName);
        },
        {
            onSuccess: (data) => {
                setValue('referrallinks', [...getValues().referrallinks, ...data]);
            },
            onError: (err: AxiosError<AxiosErrorResponse>) => {
                handleRequestError(err);
            },
            enabled: false,
            retry: false,
        }
    );
    const autoFillRefferallink = () => getDefaultReferalLinksQuery.refetch();

    const addRefferallink = () => {
        setValue('referrallinks', [...getValues().referrallinks, emptyItem]);
    };

    const deleteRefferallink = (id: number | null, ids: number) => {
        setValue(
            'referrallinks',
            getValues().referrallinks.map((x, i) => (i === ids ? { ...x, isDeleted: true } : x))
        );
    };

    const referrallinks = watch('referrallinks');
    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (
                name != null &&
                (name.indexOf('referrallinks') > -1 || name.indexOf('urlBase') > -1)
            ) {
                refreshRefLink();
            }
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    return (
        <>
            {referrallinks && referrallinks.length ? (
                referrallinks.map(
                    (item, ids) =>
                        !item.isDeleted && (
                            <Card className="mb-3" key={ids}>
                                <CardHeader className="leadgen-modal__card-header">
                                    <>
                                        {item.id ? `#${item.id}` : t('BackOfficeNew')}
                                        <Button
                                            color="danger"
                                            onClick={() => deleteRefferallink(item.id, ids)}
                                            disabled={disabled}
                                        >
                                            <FontAwesomeIcon icon={faTrashCan} />
                                        </Button>
                                    </>
                                </CardHeader>
                                <CardBody>
                                    <Row>
                                        <Col>
                                            <ValidatedInput
                                                name={`referrallinks[${ids}].utmTag`}
                                                label={t('BackOfficeUtmTag')}
                                                disabled={disabled}
                                            />
                                        </Col>
                                        <Col>
                                            <ValidatedInput
                                                name={`referrallinks[${ids}].utmValue`}
                                                label={t('BackOfficeUtmValue')}
                                                disabled={disabled}
                                            />
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        )
                )
            ) : (
                <></>
            )}

            {referrallinks && referrallinks.filter((i) => !i.isDeleted).length === 0 && (
                <Button
                    className="me-3"
                    color="secondary"
                    onClick={autoFillRefferallink}
                    disabled={disabled || getDefaultReferalLinksQuery.isFetching}
                >
                    <FontAwesomeIcon icon={faPlus} />{' '}
                    <Trans i18nKey="BackOfficeReferralLinkAutoFill" />
                </Button>
            )}

            <Button
                color="secondary"
                onClick={addRefferallink}
                disabled={disabled || getDefaultReferalLinksQuery.isFetching}
            >
                <FontAwesomeIcon icon={faPlus} /> <Trans i18nKey="BackOfficeAdd" />
            </Button>

            <Row className="mt-3">
                <Col>
                    <ValidatedInput
                        name="urlBase"
                        label={t('BackOfficeUrlBase')}
                        placeholder={instanceRoot}
                    />
                </Col>
            </Row>

            <Row>
                <Col>
                    <Input
                        label={t('ReferralLink')}
                        value={refLink || ''}
                        type="textarea"
                        buttonProps={[
                            {
                                onClick: () => {
                                    if (refLink) {
                                        navigator.clipboard.writeText(refLink);
                                    }
                                },
                                children: <FontAwesomeIcon icon={faCopy} />,
                            },
                        ]}
                    />
                </Col>
            </Row>
        </>
    );
};

export default LeadgenModalReferallinks;
